import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { PhotoPicker } from "@/components/PhotoPicker";
import { OptionsEditor, cleanGroups } from "@/components/OptionsEditor";

const EMPTY = { name: "", sku: "", category: "Umum", price: "", cost: "", stock: "", min_stock: "5" };

const Field = ({ label, id, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} data-testid={`product-form-${id}`} {...props} />
  </div>
);

export const ProductFormDialog = ({ open, product, categories, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [removed, setRemoved] = useState(false);

  useEffect(() => {
    if (open) { setForm(product ? { ...product } : EMPTY); setFile(null); setRemoved(false); }
  }, [open, product]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      name: form.name, sku: form.sku, category: form.category || "Umum",
      price: Number(form.price) || 0, cost: Number(form.cost) || 0,
      stock: parseInt(form.stock, 10) || 0, min_stock: parseInt(form.min_stock, 10) || 0,
      ...cleanGroups(form),
    };
    try {
      const { data } = product ? await api.put(`/products/${product.id}`, body) : await api.post("/products", body);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/products/${data.id}/photo`, fd).catch((err) => toast.error(`Produk tersimpan, tapi foto gagal: ${errMsg(err)}`));
      } else if (removed && product?.image_path) {
        await api.delete(`/products/${data.id}/photo`);
      }
      toast.success(product ? "Produk diperbarui" : "Produk ditambahkan");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" aria-describedby={undefined} data-testid="product-form-dialog">
        <DialogTitle className="font-display">{product ? "Edit Produk" : "Tambah Produk"}</DialogTitle>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <PhotoPicker currentPath={product?.image_path} file={file} removed={removed}
              onFile={(f) => { setFile(f); setRemoved(false); }} onRemove={() => { setFile(null); setRemoved(true); }} />
          </div>
          <div className="col-span-2"><Field label="Nama Produk" id="name" required value={form.name} onChange={set("name")} /></div>
          <Field label="Kode / SKU" id="sku" value={form.sku} onChange={set("sku")} />
          <Field label="Kategori" id="category" list="category-list" value={form.category} onChange={set("category")} />
          <datalist id="category-list">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          <Field label="Harga Jual (Rp)" id="price" type="number" min="0" required value={form.price} onChange={set("price")} />
          <Field label="Harga Modal (Rp)" id="cost" type="number" min="0" value={form.cost} onChange={set("cost")} />
          <Field label="Stok" id="stock" type="number" min="0" required value={form.stock} onChange={set("stock")} />
          <Field label="Batas Stok Menipis" id="min_stock" type="number" min="0" value={form.min_stock} onChange={set("min_stock")} />
          <div className="col-span-2"><OptionsEditor form={form} setForm={setForm} /></div>
          <div className="col-span-2 flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose} data-testid="product-form-cancel">Batal</Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="product-form-submit">Simpan</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
